<template>
  <div
    v-if="selectedMethod && data"
    :class="bem({ highlight: calculatedHighlightColour })"
  >
    <div :class="bem('indicator-wrapper')">
      <VisionIndicator
        :colour="calculatedHighlightColour"
        :class="bem('indicator')"
      />
    </div>
    <div>
      <div :class="bem('title')">
        {{ calculatedPrefix }}
        <span :class="bem('highlight')">{{ calculatedDeliveryText }}</span>
        via
        <span :class="bem('plain-highlight')">{{ selectedMethod.name }}</span>
      </div>
      <div
        v-if="showCountdown"
        :class="bem('description')"
      >
        Order within
        <span :class="bem('highlight')">{{ countdown }}</span>
        <template v-if="props.data.isPrescription">
          and you upload your prescription today.
        </template>
      </div>
      <div
        v-else-if="isOnPreorder && preorderDate"
        :class="bem('description')"
      >
        <span :class="bem('plain-highlight')">
          Your order will be despatched as one
          shipment once all required parts have been restocked.
        </span>
        You can cancel your pre-order at any time prior to despatch if required.
      </div>
    </div>
    <button :class="bem('tooltip')" @click="handleToolTipClick">
      ?
    </button>
  </div>
</template>

<script setup lang="ts">
import { useShippingData } from '~/stores/shipping'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import duration from 'dayjs/plugin/duration'
import advancedFormat from 'dayjs/plugin/advancedFormat'

const bem = useBEM('ShippingTimer')
const shippingStore = useShippingData()

dayjs.extend(utc) // Required for timezone
dayjs.extend(timezone)
dayjs.extend(duration)
dayjs.extend(advancedFormat)

/**
 *
 * The data prop means we can calculate data for the timer in the parent
 * or just pass it a customisedProduct object
 *
 */
interface ShippingTimerProps {
  data: {
    isPrescription: boolean
    isOnPreorder: boolean
    preorderPartSelected: boolean
    preorderDate: string | Date
    delayedDespatchDate: string
    allPreorderPartsAsDelayedDespatch: boolean
  }
}

interface Config {
  prescriptionLeadTime: number
  shippingCutoff: number
  shippingNextDayGuaranteed: boolean
  shippingNonDespatch: string
  shippingNonShipDays: string
}

const props = withDefaults(defineProps<ShippingTimerProps>(), {
  data: Object
})

const preorderDate = computed(() => {
  if (!props.data.preorderDate) return null
  if (typeof props.data.preorderDate === 'string') {
    return dayjs(props.data.preorderDate)
  }
  return dayjs(props.data.preorderDate)
})

const isOnPreorder = computed(() => {
  return (props.data.isOnPreorder || props.data.preorderPartSelected) && !props.data.allPreorderPartsAsDelayedDespatch
})

const currentTime = ref(dayjs().tz("Europe/London"))
const {fastestMethod} = storeToRefs(shippingStore)
const {saturdayMethod} = storeToRefs(shippingStore)
const config = shippingStore.config as Config

const selectedMethod = computed(() => {
  // Change to Saturday Method if it is Friday before the Cutoff
  // and Saturday Delivery is available
  if ((currentTime.value.day() === 5 && currentTime.value.hour() < (config.shippingCutoff / 100)
  ||  (currentTime.value.day() === 4 && currentTime.value.hour() >= (config.shippingCutoff / 100)))
    && saturdayMethod.value) {
      return saturdayMethod.value
  }

  return fastestMethod.value
})

const context = computed(() => {
  // Only delayedDespatch or Preorder if date is in the future
  if (props.data.isPrescription) {
    return 'prescription'
  } else if (isOnPreorder.value && preorderDate.value &&
    (dayjs().isBefore(
      preorderDate.value
    ))) {
    return 'preorder'
  } else if (props.data.delayedDespatchDate &&
    (dayjs().isBefore(
      dayjs(props.data.delayedDespatchDate)
    ))) {
    return 'delayedDespatch'
  }
  return 'default'
})

const nextDespatch = computed(() => {
  const cutoff = config.shippingCutoff; // including minutes ex. 1500
  let closestDespatchDay = checkDespatching(currentTime.value)
  if (
    (closestDespatchDay.isSame(currentTime.value, 'day')) &&
    (Number(currentTime.value.format("HMM")) >= cutoff)
  ) {
    closestDespatchDay = checkDespatching(currentTime.value.add(1, 'days'))
  }
  return closestDespatchDay.startOf('day').add((cutoff / 100), 'hours') // remove minutes
})

const countdown = computed(() => {
  const timeToDespatch =
    dayjs.duration(nextDespatch.value.diff(currentTime.value))
  const days = timeToDespatch.days()
  const daysText = pluralise('day', days)
  const hours = timeToDespatch.hours()
  const hoursText = pluralise('hour', hours)
  const minutes = timeToDespatch.minutes()
  const minutesText = pluralise('minute', minutes)

  let countdown = minutes ? `${minutes} ${minutesText}` : ''
  countdown =
        (hours
          ? `${hours} ${hoursText}${
            countdown ? ' ' + 'and' + ' ' : ''
            }`
          : '') + countdown
          countdown =
        (days ? `${days} ${daysText}${countdown ? ' ' : ''}` : '') + countdown

  return countdown
})

function pluralise(noun: string, count: number, suffix: string = 's') {
  return `${noun}${count !== 1 ? suffix : ''}`
}

const formattedDespatchDate = computed(() => {
  // If this is not preorder or delayed
  if (!isOnPreorder.value
  && !props.data.delayedDespatchDate) {
    if (props.data.isPrescription) {
      // Prescription is a shipped on or before lead time date
      return currentTime.value.add(config.prescriptionLeadTime, 'days')
        .format('dddd Do MMM')
    }
    // Default is despatching at next available
    return nextDespatch.value
  }

  const date = (isOnPreorder.value && preorderDate.value)
  || props.data.delayedDespatchDate

  // Preorder adds two days for processing incoming stock
  // as does preorders being shown as delayed despatch
  return (context.value === 'preorder' || props.data.allPreorderPartsAsDelayedDespatch) ?
    checkDespatching(dayjs(date.toString()).add(2, 'days')).format('Do MMMM') :
    checkDespatching(dayjs(date.toString())).format('dddd Do MMMM')
})

const estimatedDelivery = computed(() => {
  // Get delivery estimate for the min and max delivery windows
  return {
    min: transitToDate(selectedMethod.value?.transitMin),
    max: transitToDate(selectedMethod.value?.transitMax)
  }
})

const calculatedDeliveryText = computed(() => {
  if (context.value === 'default') {
    return calculateDeliveryTitle(estimatedDelivery.value)
  }

  return formattedDespatchDate.value
})

const calculatedHighlightColour = computed(() => {
  if (context.value === 'default') {
    return 'green'
  }

  return 'gradient'
})

const isGuaranteed = computed(() => {
  if (selectedMethod.value?.isoCode !== 'GB') return false
  return config.shippingNextDayGuaranteed
})

const showCountdown = computed(() => {
  if (context.value == 'preorder') {
    return false
  }
  return true
})

const calculatedPrefix = computed(() => {
  switch (context.value) {
    case 'preorder':
      return 'The estimated despatch date is';
    case 'default':
      return 'Arrives'
    default:
      return 'Shipped on or before'
  }
})

function calculateDeliveryTitle(deliveryDates:
 { min: dayjs.Dayjs; max: dayjs.Dayjs }): string {
  const today = currentTime.value;
  const onOrBy = isGuaranteed.value ? 'on' : 'by'
  const daysUntilDelivery = dayjs(deliveryDates.min).diff(today, 'day');
  if (deliveryDates.min.format() === deliveryDates.max.format()) {
    const deliveryDay = deliveryDates.min;

    if (deliveryDay.isSame(today, 'day')) {
      return 'Today';
    } else if (deliveryDay.isSame(today.add(1, 'days'), 'day')) {
      return 'Tomorrow';
    } else if (daysUntilDelivery < 6) {
      return `${onOrBy} ${deliveryDay.format("dddd")}`;
    }
    return `${onOrBy} ${deliveryDay.format('dddd Do MMMM')}`;
  }
  return `between ${deliveryDates.min.format('dddd Do MMMM')} - ${deliveryDates.max.format('dddd Do MMMM')}`;
}

function transitToDate(transit: number) {
  let datetime = nextDespatch.value;
  while (transit > 0) {
    datetime = datetime.add(1, 'days');
    datetime = checkDelivering(datetime);
    transit -= 1;
  }
  return datetime;
}

function checkDespatching(date: dayjs.Dayjs): any {
  // Avoids Eslint linting the (true) as unexpected constant condition
  const forceTrue = true;
  while (forceTrue) {
    date = checkIfWeekend(date);
    const formattedDate = date.format("YYYY-MM-DD");
    if (!config.shippingNonDespatch.includes(formattedDate) &&
      !config.shippingNonShipDays.includes(formattedDate)) {
      return date;
    }
    date = date.add(1, "day");
  }
}

function checkDelivering(date: dayjs.Dayjs): any {
  // Avoids Eslint linting the (true) as unexpected constant condition
  const forceTrue = true;
  while (forceTrue) {
    date = checkIfWeekend(date);
    const formattedDate = date.format("YYYY-MM-DD");
    if (!config.shippingNonShipDays.includes(formattedDate)) {
      return date;
    }
    date = date.add(1, "day");
  }
}

// If the date is a weekend, keep adding days until it is not
function checkIfWeekend(date: dayjs.Dayjs): dayjs.Dayjs {
  while (date.day() === 0 || (date.day() === 6 && selectedMethod.value?.type !== 'Saturday')) {
    date = date.add(1, "day");
  }
  return date;
}

function handleToolTipClick () {
  const sidebarStore = useSidebarStore()

  sidebarStore.setActiveSidebar('shipping-and-returns')
}
</script>

<style lang="postcss">
.ShippingTimer {
  @apply flex items-center gap-x-2.5 md:gap-x-2
    font-medium
    w-full
    text-sm;
}

.ShippingTimer__indicator-wrapper {
  @apply w-6 lg:w-8 flex items-center justify-center;
}

.ShippingTimer__indicator {
  @apply shrink-0;
}

.ShippingTimer__subtitle,
.ShippingTimer__description {
  @apply text-grey-600 font-medium;

  .ShippingTimer__plain-highlight {
    @apply text-off-black;
  }
}

.ShippingTimer--highlight-green {
  .ShippingTimer__highlight {
    @apply text-green;
  }

  .ShippingTimer__tooltip {
    @apply bg-green;
  }
}

.ShippingTimer--highlight-gradient {
  .ShippingTimer__highlight {
    @apply t-gradient;
  }

  .ShippingTimer__tooltip {
    @apply bg-gradient;
  }
}

.ShippingTimer__tooltip {
  @apply rounded-full aspect-square text-white text-xxs font-bold flex items-center justify-center h-4 ml-auto leading-none;
}
</style>